import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const useGetSuggestProducts = (productId) => {
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) return;

    const fetchSuggested = async () => {
      setLoading(true);
      setError(null);

      try {
        // Step 1: Get current product document to read its tags
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
          setSuggested([]);
          setLoading(false);
          return;
        }

        const productData = productSnap.data();
        const productTags = productData.tags || [];

        if (productTags.length === 0) {
          setSuggested([]);
          setLoading(false);
          return;
        }

        // Step 2: Query products with any overlapping tags (limit 20 for performance)
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('tags', 'array-contains-any', productTags));
        const querySnap = await getDocs(q);

        // Step 3: Process and rank by number of matching tags (exclude current product)
        const candidates = [];

        querySnap.forEach((docSnap) => {
          if (docSnap.id === productId) return; // exclude current product

          const data = docSnap.data();
          const tags = data.tags || [];
          const commonTagsCount = tags.filter(tag => productTags.includes(tag)).length;

          candidates.push({
            id: docSnap.id,
            ...data,
            commonTagsCount,
          });
        });

        // Step 4: Sort descending by commonTagsCount and pick top 4
        candidates.sort((a, b) => b.commonTagsCount - a.commonTagsCount);
        setSuggested(candidates.slice(0, 4));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggested();
  }, [productId]);

  return { suggested, loading, error };
};

export default useGetSuggestProducts;
